import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AppShell,
  Sidebar,
  TopBar,
  DashboardPage,
  type DashboardTabId,
  type OwnerChooserUser,
  type ProjectRow,
} from '@suvoda-uat/ui-suvoda';
import { canAuthor, currentUserId, currentUserName, signOut } from '../lib/auth.js';
import { navTargetFor, visibleNavItems } from '../lib/nav.js';
import { toProjectRow } from '../lib/dashboard.js';
import { lifecycleDestinationForStage, type LifecycleDestination } from '../lib/lifecycle.js';
import {
  useAssignableUsers,
  useConfirmTrialName,
  useRenameTrial,
  useSetTrialOwner,
  useTrials,
} from '../lib/queries.js';
import { recentProjectIds } from '../lib/recent-projects.js';

export const Route = createFileRoute('/')({
  component: DashboardRoute,
});

export type DashboardRouteContentProps = {
  readonly projects: ProjectRow[];
  /** Last week's interacted trial ids, most-recent first (UAT-01). */
  readonly recentProjectIds?: readonly string[];
  /** Initially active tab — 'all' when the viewer's identity is unknown. */
  readonly defaultTabId?: DashboardTabId;
  /** Absent for a Viewer — the create CTA is hidden (FR-017); server also gates. */
  readonly onCreateNew?: () => void;
  readonly onOpenDestination: (destination: LifecycleDestination) => void;
  /** F-020 (R5): rename a project from its card; absent ⇒ no affordance. */
  readonly onRenameProject?: (id: string, name: string) => void;
  /** F-033 (FR-015): confirm a provisional system-derived name; absent ⇒ no Confirm. */
  readonly onConfirmProject?: (id: string) => void;
  /** F-139 (R9): the shared assignable-owner list; absent ⇒ owner read-only. */
  readonly assignableUsers?: readonly OwnerChooserUser[];
  /** F-139 (R1): reassign a project's owner — `(projectId, newOwnerId)`. */
  readonly onReassignOwner?: (id: string, ownerId: string) => void;
};

export function DashboardRouteContent({
  projects,
  recentProjectIds,
  defaultTabId,
  onCreateNew,
  onOpenDestination,
  onRenameProject,
  onConfirmProject,
  assignableUsers,
  onReassignOwner,
}: DashboardRouteContentProps) {
  const openProject = (id: string): void => {
    const project = projects.find((candidate) => candidate.id === id);
    if (project === undefined) return;
    onOpenDestination(lifecycleDestinationForStage(project.status, project.id));
  };

  const exportProject = (id: string): void => {
    const project = projects.find((candidate) => candidate.id === id);
    if (project?.exportAvailability?.kind !== 'active') return;
    onOpenDestination({ to: '/uat', search: { trialId: project.id } });
  };

  return (
    <DashboardPage
      projects={projects}
      {...(recentProjectIds === undefined ? {} : { recentProjectIds })}
      {...(defaultTabId === undefined ? {} : { defaultTabId })}
      {...(onCreateNew === undefined ? {} : { onCreateNew })}
      onOpenProject={openProject}
      onExportProject={exportProject}
      {...(onRenameProject === undefined ? {} : { onRenameProject })}
      {...(onConfirmProject === undefined ? {} : { onConfirmProject })}
      {...(assignableUsers === undefined || onReassignOwner === undefined
        ? {}
        : { assignableUsers, onReassignOwner })}
    />
  );
}

function DashboardRoute() {
  const navigate = useNavigate();
  const trials = useTrials();
  const renameTrial = useRenameTrial();
  const confirmTrialName = useConfirmTrialName();
  const setTrialOwner = useSetTrialOwner();
  // F-139 (R9): fetch the assignable-owner list ONCE, shared across every card
  // (flat-team scale). Editor-tier only — viewers can't reassign (the endpoint
  // is editor-gated), so the query is disabled for them.
  const assignableUsers = useAssignableUsers({ enabled: canAuthor() });
  const viewerUserId = currentUserId();
  // Real created Trials (minimal ListTrials, T053) replace the static DEMO_PROJECTS.
  const projects = (trials.data?.items ?? []).map((trial) => toProjectRow(trial, viewerUserId));

  const openDestination = (destination: LifecycleDestination): void => {
    switch (destination.to) {
      case '/upload':
        void navigate({ to: '/upload', search: destination.search });
        return;
      case '/verification':
        void navigate({ to: '/verification', search: destination.search });
        return;
      case '/uat':
        void navigate({ to: '/uat', search: destination.search });
        return;
    }
  };

  return (
    <AppShell
      sidebar={
        <Sidebar
          items={visibleNavItems()}
          activeId="dashboard"
          onNavigate={(id) => {
            const to = navTargetFor(id);
            if (to !== undefined) void navigate({ to });
          }}
          onSignOut={() => signOut()}
        />
      }
      topbar={<TopBar label="DASHBOARD" userName={currentUserName()} onSignOut={() => signOut()} />}
    >
      <DashboardRouteContent
        projects={projects}
        recentProjectIds={recentProjectIds(viewerUserId, Date.now())}
        defaultTabId={viewerUserId === undefined ? 'all' : 'mine'}
        {...(canAuthor()
          ? { onCreateNew: () => navigate({ to: '/upload', search: { trialId: '' } }) }
          : {})}
        {...(canAuthor()
          ? {
              onRenameProject: (id: string, name: string) =>
                renameTrial.mutate({ trialId: id, name }),
              onConfirmProject: (id: string) => confirmTrialName.mutate({ trialId: id }),
              // F-139 (R1/R9): editor-tier gets the owner picker + the per-card
              // reassign callback (the SPA authz mirror, R4).
              assignableUsers: assignableUsers.data?.items ?? [],
              onReassignOwner: (id: string, ownerId: string) =>
                setTrialOwner.mutate({ trialId: id, ownerId }),
            }
          : {})}
        onOpenDestination={openDestination}
      />
    </AppShell>
  );
}
